import MiniSearch from "minisearch";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

import type { VisitDisplay } from "../components/MedicalChronology/Visits/types";
import { getCitationFileLabel } from "../components/MedicalChronology/Visits/utils/visitFormatting";
import {
  FILTER_OPTIONS,
  searchWithWordDropFallback,
  tokenizeSearchText,
} from "../components/MedicalChronology/utils/searchOptions";
import { formatDate } from "../utils";
import { useMedChronServices } from "./useMedChronServices";

interface VisitSearchDocument {
  id: string;
  visit_date_text: string;
  visit_type_name: string;
  provider_name: string;
  facility_name: string;
  visit_subtype: string;
  detail_categories: string;
  detail_texts: string;
  citation_file_names: string;
}

const SEARCHABLE_DOCUMENT_FIELDS: (keyof Omit<VisitSearchDocument, "id">)[] = [
  "visit_date_text",
  "visit_type_name",
  "provider_name",
  "facility_name",
  "visit_subtype",
  "detail_categories",
  "detail_texts",
  "citation_file_names",
];

const MINISEARCH_OPTIONS: ConstructorParameters<typeof MiniSearch<VisitSearchDocument>>[0] = {
  idField: "id",
  fields: [
    "visit_date_text",
    "visit_type_name",
    "provider_name",
    "facility_name",
    "visit_subtype",
    "detail_categories",
    "detail_texts",
    "citation_file_names",
  ],
  tokenize: tokenizeSearchText,
  // storeFields intentionally absent: we only need matched ids; all display data
  // comes from the visits state array, not the index.
};

export interface UseVisitsResult {
  visits: VisitDisplay[];
  filteredVisits: VisitDisplay[];
  isLoading: boolean;
  setVisits: React.Dispatch<React.SetStateAction<VisitDisplay[]>>;
}

export function useVisits(
  caseId: string,
  versionId: string | null,
  query: string,
  formatVisitType: (id: string) => string,
  fileMapping: Record<string, string>
): UseVisitsResult {
  const [, medChronServiceV2] = useMedChronServices();
  const [visits, setVisits] = useState<VisitDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!versionId) return;

    let cancelled = false;

    const fetchVisits = async () => {
      setIsLoading(true);
      try {
        const data = await medChronServiceV2.getVisits(caseId, versionId);
        if (!cancelled) {
          setVisits(data as VisitDisplay[]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching chronology data:", error);
          toast.error("Error loading chronology data");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchVisits();

    return () => {
      cancelled = true;
    };
  }, [caseId, versionId, medChronServiceV2]);

  const { index, documentsById } = useMemo(() => {
    const idx = new MiniSearch<VisitSearchDocument>(MINISEARCH_OPTIONS);
    const documents: VisitSearchDocument[] = visits.map((v) => ({
      id: v.visit_id,
      visit_date_text: formatDate(v.visit_date) ?? "",
      visit_type_name: formatVisitType(v.visit_type_id),
      provider_name: v.provider_name ?? "",
      facility_name: v.facility_name ?? "",
      visit_subtype: v.visit_subtype ?? "",
      // allVisits is only populated by groupTherapyVisits in VisitsView (after filtering).
      // At this stage visits are always raw/uncollapsed, so we index the single visit's details.
      detail_categories: (v.med_visit_details ?? []).map((d) => d.visit_detail_category).join(" "),
      detail_texts: (v.med_visit_details ?? []).map((d) => d.visit_details).join(" "),
      citation_file_names: v.citations.map((c) => getCitationFileLabel(c, fileMapping)).join(" "),
    }));
    idx.addAll(documents);
    return { index: idx, documentsById: new Map(documents.map((d) => [d.id, d])) };
  }, [visits, formatVisitType, fileMapping]);

  const filteredVisits = useMemo(() => {
    const trimmed = (query || "").trim();

    // Empty / whitespace query → return everything
    if (!trimmed) return visits;

    const { results } = searchWithWordDropFallback(index, trimmed, FILTER_OPTIONS);
    const scoreById = new Map(results.map((r) => [r.id, r.score]));

    const queryPhrase = tokenizeSearchText(trimmed).join(" ");
    const hasExactPhrase = (visitId: string): boolean => {
      const doc = documentsById.get(visitId);
      if (!doc) return false;
      const text = SEARCHABLE_DOCUMENT_FIELDS.map((field) => doc[field]).join(" ");
      return tokenizeSearchText(text).join(" ").includes(queryPhrase);
    };

    const matched = visits.filter((v) => scoreById.has(v.visit_id));
    const exactPhraseById = new Map(matched.map((v) => [v.visit_id, hasExactPhrase(v.visit_id)]));

    // Exact phrase matches rank first; MiniSearch's relevance score (already reflecting
    // AND-combined word co-occurrence via BM25) breaks ties among the rest.
    return matched.sort((a, b) => {
      const aHasPhrase = exactPhraseById.get(a.visit_id) ?? false;
      const bHasPhrase = exactPhraseById.get(b.visit_id) ?? false;
      if (aHasPhrase !== bHasPhrase) return aHasPhrase ? -1 : 1;
      return (scoreById.get(b.visit_id) ?? 0) - (scoreById.get(a.visit_id) ?? 0);
    });
  }, [visits, query, index, documentsById]);

  return { visits, filteredVisits, isLoading, setVisits };
}
